import { Injectable, effect, signal } from '@angular/core';
import { IUrlSegment } from '../types/infrastructure/interfaces/IUrlSegment';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { AppstateService } from './appstate.service';
import { Pages } from '../types/infrastructure/enums/Pages';
import { environment } from 'src/environments/environment';
import { CustomMenuItem } from '../types/infrastructure/CustomMenuItem';
import { DataService } from './data.service';
import { AppSettings } from './app.settings';

@Injectable({
	providedIn: 'root',
})
export class NavigationService {


	public iconMenuItems: CustomMenuItem[] =
		[new CustomMenuItem(0, false, 'Details', 'wallet', false, false, 'details'),
		new CustomMenuItem(1, false, 'Kostenherleitung', 'euro_symbol', false, false, 'kostenherleitung'),
		new CustomMenuItem(2, false, 'Risiken und Chancen', 'info', false, false, 'risikenchancen'),
		new CustomMenuItem(3, false, 'Freigabe', 'done_all', false, false, 'freigabe'),
		new CustomMenuItem(4, false, 'Historie', 'device_reset', false, true, 'history'),
		new CustomMenuItem(5, false, 'Projekt-Info', 'dock_to_left', false, false, 'sidepanel'),
		new CustomMenuItem(6, false, 'Budgetantrag-Dashboard', 'wallet', false, true, '_'),
		];


	public budgetRoute = '';

	constructor(public appstateService: AppstateService,
		private router: Router,
		private dataService: DataService,
		private appSettings: AppSettings) {

		effect(() => {
			this.setAppStateByUrl(this.appstateService.navigationUrl(), this.appstateService.projektName());
		}, { allowSignalWrites: true });

		effect(() => {
			// Die archivierten Budgetanträg ewerden hinten rangehängt
			this.iconMenuItems = this.iconMenuItems.slice(0, 7);
			this.iconMenuItems.push(new CustomMenuItem(this.iconMenuItems.length, false, 'Projekt-Details', 'filing', false, true, 'projekte/' + this.appstateService.projektInfoId(), true));
			this.dataService.projektInfo().budgetantraege.forEach(ba => {
				var link = ba.freigabeFinal ? 'budgetantraege/' + ba.budgetantragID : 'projekte/' + this.appstateService.projektInfoId() + '/budgetantrag/' + ba.budgetantragID;
				this.iconMenuItems.push(new CustomMenuItem(this.iconMenuItems.length, false, 'Budgetantrag ' + ba.wipBezeichnung + (ba.freigabeFinal ? ' - Archiv' : ''), 'wallet', false, true, link, false));
			});
		}, { allowSignalWrites: true });

	}

	setAppStateByRouting(data: Data, activatedRoute: ActivatedRoute) {
		// assistent
		this.appstateService.navigationAssistentShow.set(!!data.assistentShow);
		if (data.assistentShow) {
			if (data.assistentBaseUrl) {
				// persist assistent base url
				this.appstateService.navigationAssistentPosition.set(0);
				this.appstateService.navigationAssistentUrl.set(activatedRoute.firstChild!.snapshot.url.join('/'));
			}
		} else {
			this.appstateService.navigationAssistentPosition.set(0);
			this.appstateService.navigationAssistentUrl.set('');
		}

		// sidepanel
		if (!data.allowSidepanel) {
			if (this.appstateService.sidepanelInfoExpanded()) this.collapseSidepanelInfo();
			if (this.appstateService.sidepanelHistoryExpanded()) this.collapseSidepanelHistory();
		}

		// navigation panel
		if (data.expandNavigationPanel) {
			this.expandNavigationPanel();
		} else {
			this.collapseNavigationPanel();
		}
	}

	setAppStateByUrl(url: string, projektname: string) {

		if (url.startsWith('/projekte')) {
			this.appstateService.navigationMain.set('projekte');
		} else if (url.startsWith('/budgetantraege')) {
			this.appstateService.navigationMain.set('budgetantraege');
		}

		this.appstateService.navigationUrl.set(url);
		this.appstateService.navigationSegements.set(this.getUrlSegements(url, projektname));
	}

	//#region Navigation

	private getActiveRoute(): ActivatedRoute {
		let route = this.router.routerState.root;
		while (route.firstChild) {
			route = route.firstChild;
		}
		return route;
	}


	// Navigation durch das Icon-Menü
	navigateFromBudgetantrag(offset: string, route: string) {
		if (route == 'history')
			this.toggleSidepanelHistory();
		else if (route == 'sidepanel')
			this.toggleSidepanelInfo();
		else if (route.startsWith("budgetantraege") || route.startsWith("projekte"))
			this.router.navigate([route]);
		else {
			const activeRoute = this.getActiveRoute();
			if (route == '_') {
				if (offset) this.router.navigate([offset], { relativeTo: activeRoute });
			}
			else {
				if (activeRoute.snapshot.url.length > 6)
					offset += '/..';
				route = offset ? offset + '/' + route : route;
				this.router.navigate([route], { relativeTo: activeRoute });
			}
		}
	}


	navigateToProjekte() {
		// reset appState and reload data
		this.appstateService.resetProjektAndBudgetarchivListe();
		this.router.navigate(['/projekte']);
	}

	navigateToBudgetantraege() {
		// reset appState and reload data
		this.appstateService.resetProjektAndBudgetarchivListe();
		this.router.navigate(['/budgetantraege']);
	}

	navigateToProjektDetails(projektId: number | null) {
		if (projektId != null) {
			this.router.navigate(['/projekte', projektId]);
		}
	}


	navigateToBudgetantragDashboardFromURL(url: string): Promise<boolean> {
		url = url.replace(environment.baseUrl , '');
		const segments = url.slice(1).split('/');

        if (segments[0] == 'budgetantraege') {
			const baId: number = Number(segments[1]);
			this.appstateService.navigationUrl.set(url);
			return this.router.navigate(['budgetantraege', baId]);
		}
		else if (segments[0] == 'projekte' && segments[2] == 'budgetantrag') {
    		const projektId: number = Number(segments[1]);
			const baId: number = Number(segments[3]);
			this.appstateService.navigationUrl.set(url);
			if (segments.length > 6) {
				const auftragID: number = Number(segments[6]);
				return this.router.navigate(['projekte', projektId, 'budgetantrag', baId, 'kostenherleitung', 'teilleistungen', auftragID]);
			}
			else {
				return this.router.navigate(['projekte', projektId, 'budgetantrag', baId]);
			}
		}
		return Promise.resolve(false);
	}

	navigateToBudgetantragDashboard(projektId: number | null, baId: number, fromComponent: Pages) {
		if (projektId == null) {
			return;
		}

		// needs to bo set for navigation from archiv
		this.appstateService.projektInfoId.set(projektId);

		if (fromComponent == Pages.PROJEKTLISTE) {
			this.router.navigate(['projekte', projektId, 'budgetantrag', baId]);
		} else if (fromComponent == Pages.PROJEKTDETAILS) {
			this.router.navigate(['projekte', projektId, 'budgetantrag', baId]);
		} else if (fromComponent == Pages.ARCHIVLISTE) {
			this.router.navigate(['/budgetantraege', baId]);
		}
	}

	navigateBackToBudgetantragDashboard(activeRoute: ActivatedRoute) {
		this.router.navigate(['..'], { relativeTo: activeRoute });
	}

	navigateToBudgetantragDetails(activeRoute: ActivatedRoute) {
		this.router.navigate(['details'], { relativeTo: activeRoute });
	}

	navigateToBudgetantragDetailsFromFreigabe(activeRoute: ActivatedRoute) {
		this.router.navigate(['../details'], { relativeTo: activeRoute });
	}

	navigateToBudgetantragKostenherleitung(activeRoute: ActivatedRoute) {
		this.router.navigate(['kostenherleitung'], { relativeTo: activeRoute });
	}

	navigateToBudgetantragKostenherleitungFromFreigabe(activeRoute: ActivatedRoute) {
		this.router.navigate(['../kostenherleitung', 'teilleistungen', 'Inkonsistent'], { relativeTo: activeRoute });
	}

	navigateToFreigabeFromKostenherleitung(activeRoute: ActivatedRoute) {
		activeRoute.snapshot.url.pop(); // remove 'Inkonsistent' from url
		activeRoute.snapshot.url.pop(); // remove 'teilleistungen' from url
		activeRoute.snapshot.url.pop();
		this.router.navigate(['freigabe'], { relativeTo: activeRoute });
	}


	navigateToBudgetantragZuordnungFromFreigabe(activeRoute: ActivatedRoute) {
		this.router.navigate(['../kostenherleitung/jahreszuordnungabsolut', 'Inkonsistent'], { relativeTo: activeRoute });
	}


	navigateToBudgetantragRisikenchancen(activeRoute: ActivatedRoute) {
		this.router.navigate(['risikenchancen'], { relativeTo: activeRoute });
	}

	navigateToBudgetantragFreigabe(activeRoute: ActivatedRoute) {
		this.router.navigate(['freigabe'], { relativeTo: activeRoute });
	}

	// endregion

	//#region NavigationPanel

	toggleNavigationPanel() {
		this.appstateService.sidenavExpanded.set(!this.appstateService.sidenavExpanded());
	}

	expandNavigationPanel() {
		this.appstateService.sidenavExpanded.set(true);
	}

	collapseNavigationPanel() {
		this.appstateService.sidenavExpanded.set(false);
	}

	//#endregion

	//#region Sidepanels

	toggleSidepanelInfo() {
		if (this.appstateService.sidepanelHistoryExpanded()) {
			this.collapseSidepanelHistory();
			setTimeout(() => {
				this.expandSidepanelInfo();
			}, 500);
		} else {
			this.appstateService.sidepanelInfoExpanded.set(!this.appstateService.sidepanelInfoExpanded());
		}
	}

	expandSidepanelInfo() {
		this.appstateService.sidepanelInfoExpanded.set(true);
	}

	collapseSidepanelInfo() {
		this.appstateService.sidepanelInfoExpanded.set(false);
	}

	toggleSidepanelHistory() {
		if (this.appstateService.sidepanelInfoExpanded()) {
			this.collapseSidepanelInfo();
			setTimeout(() => {
				this.expandSidepanelHistory();
			}, 500);
		} else {
			this.appstateService.sidepanelHistoryExpanded.set(!this.appstateService.sidepanelHistoryExpanded());
		}
	}

	expandSidepanelHistory() {
		this.appstateService.sidepanelHistoryExpanded.set(true);
	}

	collapseSidepanelHistory() {
		this.appstateService.sidepanelHistoryExpanded.set(false);
	}

	//#endregion

	getUrlSegements(url: string, projektname: string): IUrlSegment[] {

		var path = url.slice(1);
		var pathSegments = path.split('/');
		var segmentData: Array<IUrlSegment> = [];

	   // console.log('Projektname: ' + projektname);
	   //console.log('URL: ' + url);
		pathSegments.forEach((item, index) => {
			if (pathSegments[0] == 'projekte') {
				switch (index) {
					case 1: {
						// add label to projektId
						segmentData.push({ name: 'Projekt ' + projektname + ' [' + item + ']', url: path.split(item)[0] + item });
						break;
					}
					case 2: {
						// ignore '/budgetantrag'
						break;
					}
					case 3: {
						// add label to baId
						segmentData.push({ name: 'Budgetantrag ' + item, url: path.split(item)[0] + item });
						break;
					}
					default: {
						var itemName = item.replace("jahreszuordnungprozent", "Jahreszuordnung (%)").replace("jahreszuordnungabsolut", "Jahreszuordnung (€)").replace("risikenchancen", "Risken und Chancen");
						segmentData.push({ name: itemName.charAt(0).toUpperCase() + itemName.slice(1), url: path.split(item)[0] + item });
						break;
					}
				}
			} else {
				switch (index) {
					case 0: {
						// replace 'budgetantraege' with 'Archiv'
						segmentData.push({ name: 'Archiv', url: path.split(item)[0] + item });
						if (pathSegments.length > 1) {
							segmentData.push({ name: 'Projekt ' + projektname + ' [' + this.appstateService.projektInfoId() + ']', url: 'projekte/' + this.appstateService.projektInfoId() });
						}
						break;
					}
					case 1: {
						// add label to baId
						segmentData.push({ name: 'Budgetantrag ' + item, url: path.split(item)[0] + item });
						break;
					}
					default: {
						var itemName = item.replace("jahreszuordnungprozent", "Jahreszuordnung (%)").replace("jahreszuordnungabsolut", "Jahreszuordnung (€)").replace("risikenchancen", "Risken und Chancen");
						segmentData.push({ name: itemName.charAt(0).toUpperCase() + itemName.slice(1), url: path.split(item)[0] + item });
						break;
					}
				}
			}
		});

		//console.log(segmentData);
		return segmentData;
	}
}
